import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NJAutocomplete, NJIconButton, NJToastService } from '@engie-group/fluid-design-system-react';

export type AutocompleteItem = { name: string; value: string };

export function successToast(message: string) {
    NJToastService(message, { iconName: 'check_circle', dismissAfter: 4000, hasCloseIcon: true });
}

export function errorToast(message: string, title = 'Erreur de soumission') {
    NJToastService(message, { title, iconName: 'error', dismissAfter: 6000, hasCloseIcon: true });
}

/** Wraps a mutation: success toast on resolve, error toast on reject. Returns whether it succeeded. */
export async function submitWithToast(run: () => Promise<unknown>, successMessage: string) {
    try {
        await run();
        successToast(successMessage);
        return true;
    } catch (error) {
        errorToast((error as Error).message);
        return false;
    }
}

const EMPTY = <span style={{ color: 'var(--nj-core-color-grey-500)' }}>-</span>;

/**
 * Nemo `HoverableElement` + `EditableField`: read value, pencil on hover when editable,
 * then editor + check/close icon buttons (Enter submits, Escape cancels).
 */
export function InlineEditable({
    canEdit,
    label,
    display,
    renderEditor,
    onSubmit,
    onCancel,
    onStart,
}: {
    canEdit: boolean;
    label: string;
    display: ReactNode;
    renderEditor: () => ReactNode;
    onSubmit: () => Promise<boolean>;
    onCancel?: () => void;
    /** Called when entering edit mode — prefill the draft here. */
    onStart?: () => void;
}) {
    const [editing, setEditing] = useState(false);
    const [hover, setHover] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const submit = async () => {
        if (await onSubmit()) setEditing(false);
    };
    const cancel = () => {
        setEditing(false);
        onCancel?.();
    };

    useEffect(() => {
        if (!editing) return;
        const onKey = (e: KeyboardEvent) => {
            if (!ref.current?.contains(e.target as Node)) return;
            if (e.key === 'Enter' && !(e.target instanceof HTMLTextAreaElement && e.shiftKey)) void submit();
            if (e.key === 'Escape') cancel();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    if (!canEdit) return <div style={{ minWidth: 64, lineHeight: '32px' }}>{display ?? EMPTY}</div>;

    if (editing) {
        return (
            <div ref={ref} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {renderEditor()}
                <NJIconButton icon="check" label="Valider" variant="tertiary" scale="sm" onClick={() => void submit()} />
                <NJIconButton icon="close" label="Annuler" variant="tertiary" scale="sm" onClick={cancel} />
            </div>
        );
    }

    return (
        <div
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 32 }}
        >
            <div style={{ minWidth: 64, lineHeight: '32px' }}>{display ?? EMPTY}</div>
            <span style={{ visibility: hover ? 'visible' : 'hidden' }}>
                <NJIconButton icon="mode_edit" label={`Modifier ${label.toLowerCase()}`} variant="tertiary" scale="sm" onClick={() => { onStart?.(); setEditing(true); }} />
            </span>
        </div>
    );
}

/** Nemo `Tag`: grey chip; on hover (edit only) grey-300 background + close icon button. */
export function AccessTag({ label, onDelete }: { label: string; onDelete?: () => void }) {
    const [hover, setHover] = useState(false);
    const editable = Boolean(onDelete);
    return (
        <span
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                minHeight: 32,
                padding: editable && hover ? '0 0 0 8px' : '3px 8px',
                border: '1px solid var(--nj-core-color-grey-400)',
                background: editable && hover ? 'var(--nj-core-color-grey-300)' : 'var(--nj-semantic-color-background-neutral-tertiary-translucent-default)',
                color: 'var(--nj-semantic-color-text-neutral-primary-default)',
            }}
        >
            <span style={{ lineHeight: '24px' }}>{label}</span>
            {editable && hover && <NJIconButton icon="close" label={`Retirer ${label}`} variant="tertiary" scale="md" onClick={onDelete} />}
        </span>
    );
}

/** Nemo `AddButton` (outlined tertiary +) opening an autocomplete with check/close. */
export function AddWithAutocomplete({
    id,
    label,
    options,
    onAdd,
}: {
    id: string;
    label: string;
    options: string[];
    onAdd: (value: string) => Promise<boolean>;
}) {
    const [selected, setSelected] = useState<AutocompleteItem | undefined>();
    const data = options.map((o) => ({ name: o, value: o }));
    return (
        <InlineEditableAdd
            label={label}
            renderEditor={() => (
                <div style={{ width: 320 }}>
                    <NJAutocomplete
                        id={id}
                        label={label}
                        data={data}
                        value={selected}
                        onChange={setSelected}
                        listLabel={`Suggestions — ${label}`}
                        inputInstructions="Utilisez les flèches haut/bas puis Entrée pour sélectionner"
                        resultsCountMessage={(n) => `${n} résultat${n > 1 ? 's' : ''}`}
                    />
                </div>
            )}
            onSubmit={async () => {
                if (!selected) {
                    errorToast('Veuillez sélectionner un élément à ajouter', 'Erreur de validation');
                    return false;
                }
                const ok = await onAdd(selected.value);
                if (ok) setSelected(undefined);
                return ok;
            }}
            onCancel={() => setSelected(undefined)}
        />
    );
}

function InlineEditableAdd({
    label,
    renderEditor,
    onSubmit,
    onCancel,
}: {
    label: string;
    renderEditor: () => ReactNode;
    onSubmit: () => Promise<boolean>;
    onCancel: () => void;
}) {
    const [editing, setEditing] = useState(false);
    if (!editing) {
        return (
            <span style={{ display: 'inline-flex', border: '1px solid var(--nj-semantic-color-border-neutral-subtle-default)' }}>
                <NJIconButton icon="add" label={label} variant="tertiary" scale="sm" onClick={() => setEditing(true)} />
            </span>
        );
    }
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {renderEditor()}
            <NJIconButton icon="check" label="Valider" variant="tertiary" scale="sm" onClick={async () => { if (await onSubmit()) setEditing(false); }} />
            <NJIconButton icon="close" label="Annuler" variant="tertiary" scale="sm" onClick={() => { setEditing(false); onCancel(); }} />
        </div>
    );
}

/** « Propriétaire » / « Consultable par » / « Modifiable par » row content. */
export function AccessList({
    id,
    items,
    canEdit,
    addLabel,
    options,
    onAdd,
    onRemove,
}: {
    id: string;
    items: string[];
    canEdit: boolean;
    addLabel: string;
    options: string[];
    onAdd: (value: string) => Promise<boolean>;
    onRemove: (value: string) => void;
}) {
    const available = options.filter((o) => !items.includes(o));
    return (
        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '4px 0' }}>
            {items.map((item) => (
                <AccessTag key={item} label={item} onDelete={canEdit ? () => onRemove(item) : undefined} />
            ))}
            {!canEdit && items.length === 0 && EMPTY}
            {canEdit && <AddWithAutocomplete id={id} label={addLabel} options={available} onAdd={onAdd} />}
        </span>
    );
}
